/**
 * formValidation.js — frontend mirror of backend `utils/form_validation.py`.
 *
 * Single source of truth for client-side form validation. Catches
 * blank/invalid submissions BEFORE they hit the network so:
 *  - users see immediate inline feedback
 *  - the backend's 400 response is treated as a defence-in-depth
 *    layer, not the primary UX path
 *  - Zapier / Mailchimp / PandaDoc are never triggered by malformed
 *    payloads
 *
 * Mirrors the regex and required-field schemas from the backend
 * exactly so server and client agree on what's valid.
 */

// Same regex as backend — pragmatic, not exhaustive. Catches the real
// bugs (missing @, missing TLD, whitespace inside) without rejecting
// legitimate plus-addresses or quoted local parts.
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

export const cleanEmail = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const cleaned = raw.trim().toLowerCase();
  return cleaned || null;
};

export const isValidEmail = (email) => {
  if (typeof email !== "string") return false;
  return EMAIL_RE.test(email.trim().toLowerCase());
};

const isBlank = (val) => {
  if (val === null || val === undefined) return true;
  if (typeof val === "string") return val.trim().length === 0;
  if (Array.isArray(val)) return val.length === 0;
  return false;
};

/**
 * @param {object} payload  - the form values keyed by field name
 * @param {string[]} required - field names that must be non-blank
 * @returns {{ ok: boolean, missing: string[], emailField: string }}
 *   When ok=false, `missing` contains every blank required field name
 *   so the UI can highlight all of them at once.
 */
export const validateRequired = (payload, required) => {
  const missing = required.filter((k) => isBlank(payload?.[k]));
  return { ok: missing.length === 0, missing };
};

/**
 * One-shot guard. Returns:
 *   { ok: true, cleaned } when payload is valid (cleaned has lowercase email)
 *   { ok: false, error: <string>, fields?: <string[]> } otherwise
 *
 * Callers should toast `error` and bail out without calling the API.
 */
export const guardSubmission = (payload, required, { emailField = "email" } = {}) => {
  if (!payload || typeof payload !== "object") {
    return { ok: false, error: "Invalid form data" };
  }
  const { ok, missing } = validateRequired(payload, required);
  if (!ok) {
    return {
      ok: false,
      error: `Please fill out: ${missing.join(", ")}`,
      fields: missing,
    };
  }
  if (required.includes(emailField) || payload[emailField]) {
    const cleaned = cleanEmail(payload[emailField]);
    if (!isValidEmail(cleaned)) {
      return {
        ok: false,
        error: "Please enter a valid email like name@example.com",
        fields: [emailField],
      };
    }
    return { ok: true, cleaned: { ...payload, [emailField]: cleaned } };
  }
  return { ok: true, cleaned: { ...payload } };
};

// Required-field schemas — match backend exactly.
export const REQUIRED_CUSTOMER_WAITLIST = ["name", "email", "phone", "vehicle_type"];
export const REQUIRED_VENDOR_APPLY = [
  "first_name", "last_name", "email", "phone",
  "business_name", "service_area", "vendor_type", "selected_tier",
];
export const REQUIRED_INVESTOR_INQUIRY = ["name", "email", "phone", "inquiry_type"];
export const REQUIRED_FRANCHISE_INQUIRY = ["name", "email", "phone", "inquiry_type"];
